import { useEffect, useState } from "react";
import ThemeToggle from "@/components/ThemeToggle";
import { formatFullWIBTitik } from "@/lib/datetime";
import logoTerang from "../assets/aura-logo-terang.png";
import logoGelap from "../assets/aura-logo-gelap.png";
import { TAGLINE } from "@/lib/brand";

// Kelas tombol bersama halaman login & verifikasi -- tinggi 52px (>= 48px target
// sentuh ponsel). Tombol utama: gradasi 2 warna brand resmi dgn teks GELAP
// (#00B9AD 5.88:1, #60C0D0 6.84:1 -- lihat komentar :root di index.css).
// Animasi hover/tekan (garis bawah tumbuh) ada di kelas .auth-btn* di index.css.
// Cincin fokus memakai warna teks utama (foreground) supaya jelas di terang & gelap.
const AUTH_BUTTON_BASE =
    "auth-btn w-full h-[52px] rounded-xl font-bold tracking-wider "
    + "focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-card";
export const AUTH_PRIMARY_BUTTON_CLASS =
    `${AUTH_BUTTON_BASE} auth-btn-primary bg-gradient-to-br from-[#00B9AD] to-[#60C0D0] `
    + "text-[#0F2E30] text-base shadow-sm";
export const AUTH_SECONDARY_BUTTON_CLASS =
    `${AUTH_BUTTON_BASE} auth-btn-secondary border border-border text-slate-700 text-[15px]`;

// Indikator loading tombol MASUK: 8 titik melingkar (diameter 22px, titik 4px)
// warna teks tombol, berputar searah jarum jam (animasi .auth-spinner, index.css).
// Opacity titik menurun ke belakang supaya putarannya terlihat; saat
// prefers-reduced-motion, diam dgn dua titik redup (.auth-spinner-dim).
const SPINNER_DOTS = 8;
const SPINNER_RADIUS = 9; // pusat 11 + jari2 9 + titik 2 = tepi 22px
const SPINNER_DOT_OPACITY = [1, 0.22, 0.28, 0.36, 0.46, 0.58, 0.7, 0.85];

export function AuthLoadingIndicator({ label = "Memproses" }) {
    return (
        <>
            <svg aria-hidden="true" width="22" height="22" viewBox="0 0 22 22" className="auth-spinner !h-[22px] !w-[22px]">
                {Array.from({ length: SPINNER_DOTS }, (_, i) => {
                    const angle = (i * 2 * Math.PI) / SPINNER_DOTS;
                    return (
                        <circle
                            key={i}
                            cx={11 + SPINNER_RADIUS * Math.sin(angle)}
                            cy={11 - SPINNER_RADIUS * Math.cos(angle)}
                            r="2"
                            fill="currentColor"
                            opacity={SPINNER_DOT_OPACITY[i]}
                            className={i === 1 || i === 2 ? "auth-spinner-dim" : undefined}
                        />
                    );
                })}
            </svg>
            <span className="sr-only">{label}</span>
        </>
    );
}
// Judul kartu ("Selamat Datang", "Autentikator"): teal keluarga tombol MASUK lewat
// token --brand-turq-text (index.css) -- otomatis berganti nuansa di mode gelap.
export const AUTH_TITLE_CLASS = "font-extrabold leading-tight text-[color:var(--brand-turq-text)]";
// Label kolom (EMAIL, KATA SANDI, kode authenticator): token teal yang sama dgn judul
// -- 4.58:1 (terang) & 6.71:1 (gelap) vs kartu, lolos 4.5:1 utk teks kecil.
export const AUTH_LABEL_CLASS = "text-[color:var(--brand-turq-text)] text-xs font-bold uppercase tracking-wider";

// Kolom input dgn kotak ikon di sisi kiri (amplop/gembok). `children` adalah
// <Input>/<PasswordInput> tanpa border sendiri; bingkai & fokus ada di sini.
export function AuthIconField({ icon: Icon, children }) {
    return (
        <div className="flex h-[52px] items-stretch overflow-hidden rounded-xl border border-input bg-card focus-within:ring-2 focus-within:ring-ring">
            <span aria-hidden="true" className="flex w-[52px] shrink-0 items-center justify-center border-r border-input bg-muted text-slate-600">
                <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">{children}</div>
        </div>
    );
}

// Jam live di footer. Sumber waktu SAMA dgn halaman login sebelumnya (jam perangkat
// pengguna yg dikonversi ke WIB lewat lib/datetime) -- hanya format tampilannya baru.
function AuthClock() {
    const [now, setNow] = useState(() => formatFullWIBTitik());
    useEffect(() => {
        const t = setInterval(() => setNow(formatFullWIBTitik()), 1000);
        return () => clearInterval(t);
    }, []);
    return <span className="tabular-nums" data-testid="login-clock">Waktu: {now}</span>;
}

// Kerangka halaman login & verifikasi 2 langkah: logo di atas, kartu di tengah,
// footer UFP + jam di bawah, tombol tema di pojok kanan atas.
// `lebar` (hanya halaman publik Survei): kartu melebar di layar besar -- <768px tetap 440px (sama dgn login),
// 768-1023px maks 720px, >=1024px maks 920px. Tanpa `lebar` tampilan TIDAK berubah (login/TOTP).
const KELAS_MAIN_LEBAR = "max-w-[440px] md:max-w-[720px] lg:max-w-[920px]";
const KELAS_KARTU_LEBAR = "md:px-10 md:py-10 lg:px-14";

export default function AuthShell({ children, lebar = false }) {
    return (
        <div className="auth-bg relative flex min-h-screen flex-col items-center justify-between gap-7 px-4 pb-7 pt-20 sm:px-5 sm:pt-12">
            <main className={`my-auto flex w-full ${lebar ? KELAS_MAIN_LEBAR : "max-w-[440px]"} flex-col gap-6`}>
                <div className="flex flex-col items-center gap-3 text-center">
                    <img src={logoTerang} alt="AURA" className="block h-auto w-[188px] dark:hidden" />
                    <img src={logoGelap} alt="AURA" className="hidden h-auto w-[188px] dark:block" />
                    <p className="text-sm text-slate-600 text-balance" data-testid="auth-tagline">{TAGLINE}</p>
                </div>
                <div className={`rounded-3xl bg-card px-5 py-8 text-card-foreground shadow-[0_1px_2px_rgba(15,46,48,0.06),0_18px_48px_rgba(15,46,48,0.10)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3),0_18px_48px_rgba(0,0,0,0.35)] sm:px-8 sm:py-9${lebar ? ` ${KELAS_KARTU_LEBAR}` : ""}`}>
                    {children}
                </div>
            </main>

            <footer className="w-full max-w-[440px] text-center text-xs leading-relaxed text-slate-600">
                <div className="font-bold">Unit Fungsional Pendidikan RSCM © 2026</div>
                <AuthClock />
            </footer>

            {/* Pemilih tema: menu tema yg sama dgn di dalam aplikasi (ThemeContext,
                kunci `simonev_theme_mode`), jadi pilihan di sini tetap berlaku setelah
                login & sebaliknya. Diletakkan terakhir di DOM supaya urutan Tab tetap
                mulai dari kolom form. */}
            <div className="absolute right-4 top-4">
                <ThemeToggle variant="auth" />
            </div>
        </div>
    );
}
